import { z } from 'zod';
import { INVENTORY_COST_KINDS, VENDOR_PAYMENT_STATUSES } from '../constants/purchasing.js';
import { VENDOR_RETURN_STATUSES } from '../constants/vendor-returns.js';
import { moneyCentsSchema, patchableSchema, uuidSchema } from './common.js';
/**
 * One cost line, attached to a serialized unit (an add-on, or the purchase
 * row intake writes automatically) or a product (a counted-stock purchase).
 * `unitId`/`productId`/`kind` are set once at creation and never change —
 * moving a cost to a different target or reclassifying it is a delete +
 * recreate, not a PATCH, so the history stays honest about what was recorded
 * and when.
 */
const inventoryCostBaseSchema = z.object({
    unitId: uuidSchema.nullable().default(null),
    productId: uuidSchema.nullable().default(null),
    kind: z.enum(INVENTORY_COST_KINDS).default('addon'),
    label: z.string().trim().min(1).max(120),
    amountCents: moneyCentsSchema,
    /** Only meaningful for a product-level purchase of counted stock. */
    qty: z.number().int().positive().nullable().default(null),
    vendor: z.string().trim().max(120).nullable().default(null),
    invoiceRef: z.string().trim().max(64).nullable().default(null),
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES).default('unpaid'),
    note: z.string().trim().max(500).nullable().default(null),
});
export const createInventoryCostSchema = inventoryCostBaseSchema
    .extend({
    /** Optional only when a component is named — then it defaults to the
     *  part's cost × qty (t1139). */
    amountCents: moneyCentsSchema.nullable().default(null),
    /** A counted part fitted to this device (t1139): taken out of stock at
     *  `componentLocationId`, and its cost lands here as the add-on's amount.
     *  All three are set together or not at all. */
    componentProductId: uuidSchema.nullable().default(null),
    componentLocationId: uuidSchema.nullable().default(null),
    componentQty: z.number().int().positive().max(1000).nullable().default(null),
    /** t1317: a cost added from a sold order line. The device or product is
     *  taken from that line, and the cost is always an add-on. */
    orderItemId: uuidSchema.nullable().default(null),
})
    .refine((v) => v.unitId !== null || v.productId !== null || v.orderItemId !== null, {
    message: 'Attach this cost to a device, a product or a sold item',
    path: ['unitId'],
})
    .refine((v) => v.kind !== 'addon' || v.unitId !== null || v.orderItemId !== null, {
    message: 'Add-on costs attach to a specific device',
    path: ['unitId'],
})
    .refine((v) => v.orderItemId === null || v.componentProductId === null, {
    message: 'A part from inventory cannot be fitted from a sold item',
    path: ['orderItemId'],
})
    .refine((v) => {
    const set = [v.componentProductId, v.componentLocationId, v.componentQty].filter((x) => x !== null).length;
    return set === 0 || set === 3;
}, {
    message: 'Choose the part, where it comes from and how many',
    path: ['componentProductId'],
})
    .refine((v) => v.componentProductId === null || (v.kind === 'addon' && v.unitId !== null), {
    message: 'Only an add-on on a specific device can use a part from inventory',
    path: ['componentProductId'],
})
    .refine((v) => v.componentProductId !== null || v.amountCents !== null, {
    message: 'Enter an amount',
    path: ['amountCents'],
});
export const updateInventoryCostSchema = patchableSchema(inventoryCostBaseSchema.omit({ unitId: true, productId: true, kind: true }));
export const inventoryCostDocumentMetaSchema = z.object({
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    createdAt: z.iso.datetime(),
});
export const inventoryCostSchema = z.object({
    id: uuidSchema,
    unitId: uuidSchema.nullable(),
    unitImei: z.string().nullable(),
    productId: uuidSchema.nullable(),
    productName: z.string().nullable(),
    /** The sold order line this cost was added from, if any (t1317). */
    orderItemId: uuidSchema.nullable(),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    kind: z.enum(INVENTORY_COST_KINDS),
    label: z.string(),
    amountCents: z.number().int(),
    qty: z.number().int().nullable(),
    /** The counted part fitted to the device, when this add-on took one out of stock (t1139). */
    componentProductId: uuidSchema.nullable(),
    componentProductName: z.string().nullable(),
    componentLocationId: uuidSchema.nullable(),
    componentLocationName: z.string().nullable(),
    componentQty: z.number().int().nullable(),
    vendor: z.string().nullable(),
    invoiceRef: z.string().nullable(),
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES),
    note: z.string().nullable(),
    document: inventoryCostDocumentMetaSchema.nullable(),
    /** The live vendor return raised for this line's device or against this line (t1154). */
    vendorReturn: z.object({ id: uuidSchema, status: z.enum(VENDOR_RETURN_STATUSES) }).nullable(),
    createdAt: z.iso.datetime(),
});
/** Removing a fitted part's add-on: whether the part goes back on the shelf (t1139). */
export const deleteInventoryCostQuerySchema = z.object({
    restock: z.enum(['true', 'false']).default('true'),
});
export const inventoryCostListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    unitId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    orderId: uuidSchema.optional(),
    kind: z.enum(INVENTORY_COST_KINDS).optional(),
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES).optional(),
    vendor: z.string().trim().max(120).optional(),
});
/**
 * Flips a set of cost rows to a new payment status in one action — e.g.
 * marking everything on one vendor invoice as paid. Target either an
 * explicit set of rows, or every row sharing a vendor + invoice reference.
 */
export const bulkCostStatusSchema = z
    .object({
    ids: z.array(uuidSchema).min(1).max(200).optional(),
    vendor: z.string().trim().min(1).max(120).optional(),
    invoiceRef: z.string().trim().min(1).max(64).optional(),
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES),
})
    .refine((v) => (v.ids && v.ids.length > 0) || (v.vendor && v.invoiceRef), {
    message: 'Provide either a set of cost ids, or a vendor and invoice reference',
    path: ['ids'],
});
export const bulkCostStatusResultSchema = z.object({ updated: z.array(uuidSchema) });
export const cogsQuerySchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** t1393: only sales made at this location (default: every location). */
    locationId: uuidSchema.optional(),
    /** t1393: `phones` = serialized products only. Rental income has no sale
     *  lines, so it is counted only for `all` with no location. */
    scope: z.enum(['phones', 'all']).default('all'),
});
/** t1393: the share of total profit the owner wants shown as its own figure. */
export const COGS_PROFIT_SHARE_BPS = 3000;
/** Per-sale export of the COGS report (t1046). `phones` is serialized
 *  products only; `all` adds every other sold line so the export's totals
 *  reconcile with the report's product totals. */
export const cogsExportQuerySchema = cogsQuerySchema.extend({
    scope: z.enum(['phones', 'all']).default('phones'),
});
export const cogsSalesQuerySchema = cogsExportQuerySchema;
export const cogsProductRowSchema = z.object({
    productId: uuidSchema,
    productName: z.string(),
    sku: z.string(),
    qtySold: z.number().int(),
    revenueCents: z.number().int(),
    cogsCents: z.number().int(),
    /** Card processing fees on this product's sales (t1262). */
    cardFeesCents: z.number().int(),
    /** Revenue less goods cost and card fees. */
    grossProfitCents: z.number().int(),
});
export const cogsReportSchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    totals: z.object({
        revenueCents: z.number().int(),
        cogsCents: z.number().int(),
        grossProfitCents: z.number().int(),
        unitsSold: z.number().int(),
        ordersCount: z.number().int(),
        /** Rental fees and damage fees collected in the window (t0944) — already
         *  folded into `revenueCents`/`grossProfitCents`, broken out here so the
         *  UI can show the split. Refundable deposits are never included. */
        rentalRevenueCents: z.number().int(),
        /** Card processing fees on every sale and rental payment in the window
         *  (t1262): Stripe's actual fee, or the card machine rate from Settings.
         *  Subtracted from `grossProfitCents`; `cogsCents` stays goods only. */
        cardFeesCents: z.number().int(),
        /** Stripe charges whose real fee hasn't been fetched yet — counted as $0
         *  until it arrives. */
        cardFeesPendingCount: z.number().int(),
        /** Card machine payments in the window, so the UI can say when the rate
         *  still needs entering. */
        cardMachinePaymentsCount: z.number().int(),
        /** t1393: 30% of `grossProfitCents`. */
        profitShareCents: z.number().int(),
    }),
    /** The card machine (Square) fee rate currently applied — echoed here because
     *  the Settings endpoint is a different permission. */
    cardMachineFee: z.object({
        bps: z.number().int(),
        fixedCents: z.number().int(),
    }),
    byProduct: z.array(cogsProductRowSchema),
});
/** t1393: one row per sold line — what the screen lists and the CSV exports. */
export const cogsSaleRowSchema = z.object({
    saleDate: z.string(),
    orderId: uuidSchema,
    orderNumber: z.string(),
    channel: z.string(),
    locationName: z.string().nullable(),
    productName: z.string(),
    sku: z.string(),
    productType: z.string(),
    imei: z.string().nullable(),
    serialNumber: z.string().nullable(),
    qty: z.number().int(),
    unitPriceCents: z.number().int(),
    unitCostCents: z.number().int(),
    extraCostCents: z.number().int(),
    /** Price × qty, before sales tax. */
    revenueCents: z.number().int(),
    cogsCents: z.number().int(),
    cardFeeCents: z.number().int(),
    profitCents: z.number().int(),
});
export const cogsSalesReportSchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    locationName: z.string().nullable(),
    rows: z.array(cogsSaleRowSchema),
    totals: z.object({
        qty: z.number().int(),
        revenueCents: z.number().int(),
        cogsCents: z.number().int(),
        cardFeesCents: z.number().int(),
        profitCents: z.number().int(),
        profitShareCents: z.number().int(),
    }),
});
/** Sales tax report (t1166): one calendar month, bucketed at the store's
 *  timezone. */
export const salesTaxQuerySchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a month like 2026-10'),
});
export const salesTaxCategoryRowSchema = z.object({
    taxCategoryId: uuidSchema.nullable(),
    name: z.string(),
    /** The category's current rate — what was charged is `taxCents`. */
    rateBps: z.number().int().nullable(),
    kind: z.enum(['category', 'none', 'gift_card']),
    /** Line prices before tax, net of item-level returns. */
    salesCents: z.number().int(),
    /** Sales to a tax-exempt customer that would otherwise have been taxed. */
    exemptSalesCents: z.number().int(),
    /** Sales that carried no tax and were not exempt: 0% categories, kiosk
     *  orders, gift cards, products with no category. */
    notTaxedSalesCents: z.number().int(),
    /** Sales that had tax charged: sales less the two columns above. */
    taxableSalesCents: z.number().int(),
    /** Tax collected, net of tax refunded on returns — the tax owed. */
    taxCents: z.number().int(),
});
export const salesTaxReportSchema = z.object({
    month: z.string(),
    from: z.iso.date(),
    to: z.iso.date(),
    totals: z.object({
        salesCents: z.number().int(),
        exemptSalesCents: z.number().int(),
        notTaxedSalesCents: z.number().int(),
        taxableSalesCents: z.number().int(),
        taxCents: z.number().int(),
        ordersCount: z.number().int(),
        /** Order-level amounts, shown so the report reconciles to order totals:
         *  shipping is never taxed and coupons come off after tax. */
        shippingCents: z.number().int(),
        discountCents: z.number().int(),
    }),
    byCategory: z.array(salesTaxCategoryRowSchema),
});
//# sourceMappingURL=purchasing.js.map